import type { ISODate } from "../types.js";
import { addDays, dayOfWeek, parseISODate } from "./date.js";
import { jpxClosedDays } from "./jp-holidays.js";

/**
 * Business-day calendar for the Tokyo Stock Exchange.
 * Closed days are computed per year and cached; extra closures (e.g. an
 * unscheduled market halt) can be injected through `extraClosed`.
 */
export class MarketCalendar {
  private cache = new Map<number, Set<ISODate>>();
  private extraClosed: Set<ISODate>;

  constructor(extraClosed: Iterable<ISODate> = []) {
    this.extraClosed = new Set(extraClosed);
  }

  private closedFor(y: number): Set<ISODate> {
    let s = this.cache.get(y);
    if (!s) {
      s = jpxClosedDays(y);
      this.cache.set(y, s);
    }
    return s;
  }

  isBusinessDay(d: ISODate): boolean {
    const dow = dayOfWeek(d);
    if (dow === 0 || dow === 6) return false;
    if (this.extraClosed.has(d)) return false;
    return !this.closedFor(parseISODate(d).y).has(d);
  }

  /** Next business day on or after `d`. */
  nextBusinessDay(d: ISODate): ISODate {
    let cur = d;
    while (!this.isBusinessDay(cur)) cur = addDays(cur, 1);
    return cur;
  }

  /** Previous business day on or before `d`. */
  prevBusinessDay(d: ISODate): ISODate {
    let cur = d;
    while (!this.isBusinessDay(cur)) cur = addDays(cur, -1);
    return cur;
  }

  /**
   * Move `n` business days from `d` (negative moves backwards).
   * `d` itself is not counted even if it is a business day.
   */
  addBusinessDays(d: ISODate, n: number): ISODate {
    let cur = d;
    let remaining = Math.abs(n);
    const step = n < 0 ? -1 : 1;
    while (remaining > 0) {
      cur = addDays(cur, step);
      if (this.isBusinessDay(cur)) remaining -= 1;
    }
    return cur;
  }

  /** Settlement date for a trade executed on `tradeDate` (T+2). */
  settlementDate(tradeDate: ISODate): ISODate {
    return this.addBusinessDays(tradeDate, 2);
  }
}
